import React from 'react';
import {
    Typography,
    Chip,
    Box,
    Avatar,
    Link,
    Switch,
    FormControlLabel,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { _date } from "../../../utils/helpers";

function getInitial(row) {
    const first = (row.first_name || '').trim()[0];
    const last = (row.last_name || '').trim()[0];
    return (first || last || '?').toUpperCase();
}

/** @param {string} locationSearch e.g. `?token=...` from useLocation().search */
/** @param {(row: object) => void} [onToggleModerated] */
export const StudentColumns = (locationSearch = '', onToggleModerated) => [
    {
        id: "name",
        label: "Name",
        render: (value, row) => {
            const initial = getInitial(row);
            const profileTo = `/students/${row.id}${locationSearch || ''}`;
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {row.avatar ? (
                        <Avatar
                            src={row.avatar}
                            alt={`${row.first_name} ${row.last_name}`}
                            sx={{ width: 30, height: 30, fontSize: 12, flexShrink: 0 }}
                        />
                    ) : (
                        <Avatar sx={{ width: 30, height: 30, fontSize: 12, flexShrink: 0 }}>
                            {initial}
                        </Avatar>
                    )}
                    <Box>
                        <Link
                            component={RouterLink}
                            to={profileTo}
                            underline="hover"
                            color="inherit"
                            variant="body2"
                            sx={{ fontWeight: 500, display: 'inline-block' }}
                        >
                            {row.first_name} {row.last_name}
                        </Link>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {row.email}
                        </Typography>
                    </Box>
                </Box>
            );
        }
    },
    {
        id: "purchased_courses_count",
        label: "Purchased Courses",
        render: (value, row) => (
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                {row.purchased_courses_count ?? 0}
            </Typography>
        )
    },
    {
        id: "country",
        label: "Country",
        render: (value, row) => (
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                {row.country_name || row.country || "—"}
            </Typography>
        )
    },
    {
        id: "city",
        label: "City",
        render: (value, row) => (
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                {row.city || "—"}
            </Typography>
        )
    },
    {
        id: "timezone",
        label: "Timezone",
        render: (value, row) => (
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                {row.timezone || "—"}
            </Typography>
        )
    },
    {
        id: "is_moderated",
        label: "Moderated",
        render: (value, row) => (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                        <Switch
                            size="small"
                            checked={Boolean(row.is_moderated)}
                            onChange={() => onToggleModerated?.(row)}
                            disabled={!onToggleModerated}
                        />
                    }
                    label={
                        <Typography variant="caption" color="text.secondary">
                            {row.is_moderated ? "Yes" : "No"}
                        </Typography>
                    }
                    labelPlacement="end"
                />
            </Box>
        ),
    },
    {
        id: "email_verified_at",
        label: "Email Verified",
        render: (value, row) => {
            return (
                <Box sx={{ textAlign: 'center' }}>
                    <Chip 
                        label={value ? "Verified" : "Not Verified"} 
                        color={value ? "success" : "warning"} 
                        size="small" 
                    />
                </Box>
            );
        }
    },
    {
        id: "created_at",
        label: "Created Date",
        render: (value, row) => {
            return (
                <Typography sx={{ color: "text.secondary", textAlign: 'center' }}>
                    {_date(value, { relative: true })}
                </Typography>
            );
        }
    },
];
